import { useEffect, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { useChatStore } from './useChatStore';

/**
 * Time threshold for session reset (15 minutes in milliseconds)
 */
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * ChatSessionManager
 *
 * Monitors app state (foreground/background) and automatically resets
 * the chat session if the app has been in the background for more than
 * 15 minutes.
 *
 * This component doesn't render anything - it just manages side effects.
 */
export function ChatSessionManager() {
  const resetSession = useChatStore((state) => state.resetSession);
  const updateLastActive = useChatStore((state) => state.updateLastActive);
  const lastActiveTimestamp = useChatStore((state) => state.lastActiveTimestamp);

  const backgroundTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      // App is moving to background
      if (nextAppState === 'background' || nextAppState === 'inactive') {
        backgroundTimestampRef.current = Date.now();
      }

      // App is coming to foreground
      if (nextAppState === 'active') {
        const now = Date.now();
        const backgroundTime = backgroundTimestampRef.current;

        // If we have a background timestamp, check if 15+ minutes have passed
        if (backgroundTime !== null) {
          const timeInBackground = now - backgroundTime;

          if (timeInBackground >= SESSION_TIMEOUT_MS) {
            // Session has timed out - reset it
            resetSession();
          } else {
            // Session is still valid - update timestamp
            updateLastActive();
          }
        }

        // Clear background timestamp
        backgroundTimestampRef.current = null;
      }
    };

    // Subscribe to app state changes
    const subscription = AppState.addEventListener('change', handleAppStateChange);

    // Cleanup on unmount
    return () => {
      subscription.remove();
    };
  }, [resetSession, updateLastActive]);

  // This component doesn't render anything
  return null;
}
